import React, { useState, useEffect } from "react";
import { Form, Card, Col, Row, Alert, Spin, Table } from "antd";
import { useReportsStore } from "../../store/slices/createReportsSlice";
import moment from "moment-jalaali";
import strings from "../../utils/strings";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import DatePicker, { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import ValidatorHelper from "../helpers/ValidatorHelper";
import { message } from "antd";

const getDefaultFromDate = () => new DateObject({ calendar: persian, locale: persian_fa }).subtract(1, "month");
const getDefaultToDate = () => new DateObject({ calendar: persian, locale: persian_fa });

export default function PerformanceReportTab({ selectedUser, visible, tabId }) {
    const reports = useReportsStore();
    const [performanceForm] = Form.useForm();
    const marketerDashboardData = reports.marketerDashboardReport.data;
    const marketerDashboardLoading = reports.marketerDashboardReport.loading;
    const MarketerReportByCompany = reports.marketerDashboardReport?.data?.MarketerReportByCompany

    // Persian date states with defaults (1 month ago to today)
    const [fromDate, setFromDate] = useState(getDefaultFromDate);
    const [toDate, setToDate] = useState(getDefaultToDate);
    const [dateErrorMessage, setDateErrorMessage] = useState(null);
    // Key to force re-render of date pickers
    const [datePickerKey, setDatePickerKey] = useState(0);

    // Reset date pickers to defaults when modal opens/closes, user changes, or tab changes away
    useEffect(() => {
        if (!visible || !selectedUser || tabId !== "1") {
            setFromDate(getDefaultFromDate());
            setToDate(getDefaultToDate());
            setDateErrorMessage(null);
            performanceForm.resetFields();
            setDatePickerKey(prev => prev + 1); // Force re-render by changing key
        }
    }, [visible, selectedUser, tabId, performanceForm]);

    const layout = {
        lg: 4,
        md: 12,
        xs: 24
    };



    function convertToMoment(date) {
        if (!date) return '';
        const d = typeof date.toDate === 'function' ? date.toDate() : date;
        return moment(d).format("YYYY-MM-DD");
    }

    const validateDateRange = (showToast = true) => {
        if (!fromDate || !toDate) {
            const err = 'لطفاً هر دو تاریخ شروع و پایان را انتخاب کنید';
            setDateErrorMessage(err);
            if (showToast) {
                message.error(err);
            }
            return false;
        }

        const dStart = typeof fromDate.toDate === 'function' ? fromDate.toDate() : fromDate;
        const dEnd = typeof toDate.toDate === 'function' ? toDate.toDate() : toDate;
        const startMoment = moment(dStart);
        const endMoment = moment(dEnd);

        if (startMoment.isAfter(endMoment)) {
            const err = 'تاریخ شروع نمی‌تواند بعد از تاریخ پایان باشد';
            setDateErrorMessage(err);
            if (showToast) {
                message.error(err);
            }
            return false;
        }

        const maxAllowedEndDate = startMoment.clone().add(6, 'jMonths');
        const isExceedingSixMonths = endMoment.isAfter(maxAllowedEndDate);

        if (isExceedingSixMonths) {
            const err = 'بازه زمانی انتخابی نمی‌تواند بیشتر از 6 ماه باشد';
            setDateErrorMessage(err);
            if (showToast) {
                message.error(err);
            }
            return false;
        }

        setDateErrorMessage(null);
        return true;
    };

    const fetchPerformanceData = async () => {
        if (!selectedUser) return;

        try {
            const fromDateStr = convertToMoment(fromDate);
            const toDateStr = convertToMoment(toDate);

            await reports.getMarketerDashboardReport(
                selectedUser.UsrUserID,
                fromDateStr,
                toDateStr
            );
        } catch (error) {
        }
    };

    function onPerformanceFormFinish(values) {
        // اعتبارسنجی فقط هنگام کلیک روی جستجو انجام می‌شود. در صورت خطا، ارور نمایش داده شده و API فراخوانی نمی‌شود
        if (!validateDateRange(true)) {
            return;
        }
        fetchPerformanceData();
    }

    // هنگام تغییر تاریخ‌ها، خطای قبلی پاک می‌شود تا پیش از کلیک روی جستجو خطایی مزاحم کاربر نشود
    useEffect(() => {
        setDateErrorMessage(null);
    }, [fromDate, toDate]);

    const CardItem = ({ value, description, isInstallment }) => {
        const getDescription = () => {
            if (isInstallment) {
                switch (description) {
                    case "TotalUnpaidInstallments": return "مجموع اقساط پرداخت نشده";
                    case "MaxCreditAmount": return "سقف خرید اقساط";
                    case "RemainingToCreditAmount": return "میزان مبلغ باقی مانده تا سقف";
                    default: return description;
                }
            } else {
                switch (description) {
                    case "SumPaymentAmount": return "حق بیمه پرداختی";
                    case "SumActualAmount": return "حق بیمه تولیدی";
                    case "IssuedCount": return "تعداد بیمه نامه های صادر شده";
                    case "SumDiscountAmount": return "درآمد";
                    default: return description;
                }
            }
        };

        const formatValue = () => {
            if (description === "IssuedCount") {
                return `${strings.getFarsiNum(value || "۰")} عدد`;
            }

            // Handle null values
            if (value === null || value === undefined) {
                return `${strings.getFarsiNum("۰")} تومان`;
            }

            return `${strings.getFarsiNum(((value) / 10).toLocaleString())} تومان`;
        };

        return (
            <Card
                size="small"
                style={{
                    margin: '8px',
                    background: isInstallment ? '#f0f8ff' : '#f9f9f9',
                    border: isInstallment ? '1px solid #1890ff' : '1px solid #d9d9d9'
                }}
            >
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: isInstallment ? '#1890ff' : '#52c41a',
                        marginBottom: '8px'
                    }}>
                        {formatValue()}
                    </div>
                    <div style={{
                        fontSize: '12px',
                        color: '#666',
                        lineHeight: '1.4'
                    }}>
                        {getDescription()}
                    </div>
                </div>
            </Card>
        );
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const { value } = payload[0];
            return (
                <div style={{
                    background: 'var(--glass-overlay)',
                    backdropFilter: 'var(--blur-md)',
                    WebkitBackdropFilter: 'var(--blur-md)',
                    padding: '10px',
                    border: 'var(--glass-border)',
                    borderRadius: 'var(--radius-md)',
                    boxShadow: 'var(--shadow-lg)'
                }}>
                    <p style={{ margin: 0, fontWeight: 'bold' }}>{label}</p>
                    <p style={{ margin: 0, color: '#8884d8' }}>
                        {`${strings.getFarsiNum((value / 10).toLocaleString())} تومان`}
                    </p>
                </div>
            );
        }
        return null;
    };

    const ChartComponent = () => {
        const chartData = marketerDashboardData?.MarketerChartReports || [];

        if (!chartData || chartData.length === 0) {
            return (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Alert
                        type="info"
                        message="داده‌ای برای نمایش نمودار موجود نیست"
                        showIcon
                    />
                </div>
            );
        }

        return (
            <div style={{ width: '100%', height: '400px', marginTop: '20px' }}>
                <h4 style={{ textAlign: 'center', marginBottom: '20px' }}>نمودار فروش یکسال اخیر</h4>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <XAxis
                            dataKey="MonthName"
                            tick={{ fontSize: 12 }}
                            interval={0}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                        />
                        <YAxis
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => `${(value / 10000000).toFixed(0)}M`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <CartesianGrid strokeDasharray="3 3" />
                        <Bar
                            dataKey="Amount"
                            fill="#8884d8"
                            radius={[4, 4, 0, 0]}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        );
    };

    // Get data from API response or use default values
    const performanceData = marketerDashboardData || {};


    const installmentOrder = ['TotalUnpaidInstallments', 'MaxCreditAmount', 'RemainingToCreditAmount'];
    const reportOrder = ['SumPaymentAmount', 'SumActualAmount', 'IssuedCount', 'SumDiscountAmount'];

    const generatePivotData = (data) => {
        const allMonths = [
            "فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور",
            "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند"
        ];
        if (!data || data.length === 0) return { columns: [], dataSource: [] };
        const companies = [...new Set(data.map(item => item.CompanyName.trim()))];
        const monthsWithData = new Set(data.map(item => item.MonthName));

        const dynamicColumns = [
            {
                title: "شرکت بیمه",
                dataIndex: "CompanyName",
                key: "CompanyName",
                fixed: "left",
                width: 100,
            },
            ...allMonths.map(month => ({
                title: month,
                dataIndex: month,
                key: month,
                width: 100,
                align: "center",
                onHeaderCell: () => ({
                    style: monthsWithData.has(month)
                        ? { background: 'rgb(82, 196, 26)' }
                        : {},
                }),
            }))
        ];

        const dataSource = companies.map(company => {
            const row = { CompanyName: company };
            allMonths.forEach(month => {
                const record = data.find(
                    item =>
                        item.CompanyName.trim() === company &&
                        item.MonthName === month
                );
                const amount = record ? record.Amount : 0;
                row[month] = `${strings.getFarsiNum((amount / 10).toLocaleString())} `;
            });
            return row;
        });

        return { columns: dynamicColumns, dataSource };
    };


    const { columns: pivotColumns, dataSource: pivotData } =
        generatePivotData(MarketerReportByCompany || []);

    return (
        <Spin spinning={marketerDashboardLoading} tip="در حال دریافت اطلاعات...">
            <div style={{ padding: '20px' }}>
                <Card style={{ marginBottom: '20px' }}>
                    <Form
                        className="sale-filter"
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                        onFinish={onPerformanceFormFinish}
                        form={performanceForm}
                        initialValues={{
                            fromDate: getDefaultFromDate(),
                            toDate: getDefaultToDate()
                        }}
                    >
                        <Row gutter={[8]}>
                            <Col {...layout}>
                                <Form.Item
                                    name='fromDate'
                                    label='از تاریخ'
                                    rules={ValidatorHelper.required('تاریخ')}
                                >
                                    <DatePicker
                                        value={fromDate}
                                        onChange={setFromDate}
                                        calendar={persian}
                                        locale={persian_fa}
                                        format="YYYY/MM/DD"
                                        inputClass="datepicker-input"
                                        calendarPosition="bottom-right"
                                        portal
                                        zIndex={1070}
                                    />
                                </Form.Item>
                            </Col>
                            <Col {...layout}>
                                <Form.Item
                                    name='toDate'
                                    label='تا تاریخ'
                                    rules={ValidatorHelper.required('تاریخ')}
                                >
                                    <DatePicker
                                        value={toDate}
                                        onChange={setToDate}
                                        calendar={persian}
                                        locale={persian_fa}
                                        format="YYYY/MM/DD"
                                        inputClass="datepicker-input"
                                        calendarPosition="bottom-right"
                                        portal
                                        zIndex={1070}
                                    />
                                </Form.Item>
                            </Col>
                            <Col {...layout}>
                                <Form.Item label=" " colon={false}>
                                    <button
                                        disabled={marketerDashboardLoading}
                                        type="submit"
                                        className="btn primary-button waves-effect waves-light"
                                    >
                                        جستجو&nbsp;&nbsp;&nbsp;
                                        <i className={marketerDashboardLoading ? "bx bx-loader bx-spin" : "fas fa-search"}></i>
                                    </button>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                    {dateErrorMessage && (
                        <Alert
                            type="error"
                            message={dateErrorMessage}
                            style={{ marginTop: '8px' }}
                            showIcon
                        />
                    )}
                </Card>



                {marketerDashboardData && (
                    <>
                        {(marketerDashboardData.MaxCreditAmount !== null ||
                            marketerDashboardData.TotalUnpaidInstallments !== null ||
                            marketerDashboardData.RemainingToCreditAmount !== null) && (
                                <div style={{ marginBottom: '30px' }}>
                                    <h3 style={{ marginBottom: '20px', color: '#1890ff' }}>گزارش اقساط</h3>
                                    <Row gutter={[16, 16]}>
                                        {installmentOrder.map((key) => (
                                            <Col xs={24} sm={12} md={8} key={key}>
                                                <CardItem
                                                    isInstallment={true}
                                                    description={key}
                                                    value={performanceData[key]}
                                                />
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            )}

                        <div style={{ marginBottom: '30px' }}>
                            <h3 style={{ marginBottom: '20px', color: '#52c41a' }}>گزارش عملکرد</h3>
                            <Row gutter={[16, 16]}>
                                {reportOrder.map((key) => (
                                    <Col xs={24} sm={12} md={6} key={key}>
                                        <CardItem
                                            isInstallment={false}
                                            description={key}
                                            value={performanceData[key]}
                                        />
                                    </Col>
                                ))}
                            </Row>
                        </div>
                        {MarketerReportByCompany && MarketerReportByCompany.length > 0 &&
                            <Card className="mb-4">
                                <h4 style={{ textAlign: 'center', marginBottom: '20px' }}>تفکیک آمار فروش براساس شرکت‌های بیمه انتخابی بازاریاب</h4>

                                <Table
                                    className="mb-4"
                                    bordered
                                    rowKey="CompanyName"
                                    columns={pivotColumns}
                                    dataSource={pivotData}
                                    scroll={{ x: true }}
                                    pagination={false}
                                />
                            </Card>}
                        {marketerDashboardData &&
                            (!MarketerReportByCompany || MarketerReportByCompany.length === 0) && (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <Alert
                                        type="warning"
                                        message="برای بازاریاب در بازه زمانی انتخاب شده، فروشی ثبت نشده است."
                                        showIcon
                                    />
                                </div>
                            )}
                        {marketerDashboardData.MarketerChartReports && marketerDashboardData.MarketerChartReports.length > 0 && (
                            <Card>
                                <ChartComponent />
                            </Card>
                        )}
                    </>
                )}

                {!marketerDashboardLoading && !marketerDashboardData && (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Alert
                            type="info"
                            message="برای مشاهده گزارش عملکرد، لطفاً بازه زمانی مورد نظر را انتخاب کرده و دکمه جستجو را بزنید."
                            showIcon
                        />
                    </div>
                )}
            </div>
        </Spin>
    );
}

